import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table } from '../../components/Table'
import { AddCCPopup } from './AddCCPopup/AddCCPopup'
import { DeleteContractCategoriesPopup } from './DeleteContractCategoriesPopup/DeleteContractCategoriesPopup'
import { ManageAssignmentPopupWithoutItems } from './ManageAssignmentPopupWithoutItems/ManageAssignmentPopupWithoutItems'
import { ManageAssignmentWithCoOrRFPHSPresidentPopup } from './ManageAssignmentWithCoOrRFPHSPresidentPopup/ManageAssignmentWithCoOrRFPHSPresidentPopup'
import { Button, BUTTON_TYPES } from '../../components/Button'
import useRouter from '../../hooks/useRouter'
import { setLoading } from '../../redux/store/common/slice'
import { columns } from './constants'
import { changeCommunityLevel, removeCCFromUserProfile } from './api'
import { getUser } from '../../redux/store/user/getters'
import { useCRUDUserPopupProviderContext } from '../../pages/CRUDUser/Provider/CRUDUserPopupProvider'
import { useCRUDUserContext } from '../../pages/CRUDUser/Provider/CRUDUserProvider'
import { notification } from '../../components/Notification'
import { BTN_TXT, PLACEHOLDERS } from '../../constants'
import { ROLES } from '../Permission'
import { ReactComponent as Delete16 } from 'assets/svg/Delete16.svg'
import { ChangeCommunityLevelFunc, UserContractCategoriesProps } from './types'
import { Input } from '../../components/Input'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from '../../assets/svg/Clos16.svg'
import './styles.scss'
import { AxiosResponse } from 'axios'

export const UserContractCategories = ({
  role,
  userId,
  userName,
  simpleViewModeOnly,
  canSeeExtendedViewMode
}: UserContractCategoriesProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const dispatch = useDispatch()
  const {
    state: {
      totalNumber,
      pageInfo,
      sortParams,
      firstLoad,
      tableData,
      searchValue,
      debouncedSearch,
      canManageContractCategories,
      canCreateCommunityContractCategories,
      canViewCommunityContractCategories,
      canEditCommunityLevelCheckboxes,
      canViewCommunityLevelCheckboxes,
      canManageCategoryReassignment,
      selectedCC
    },
    actions: {
      getContractCategories,
      setPageInfo,
      setSelectedCC,
      setSearchValue
    }
  } = useCRUDUserContext()

  const { deleteCCPopup } = useCRUDUserPopupProviderContext()
  const [singleSelectedCC, setSingleSelectedCC] = useState<string | null>(null)

  const {
    query: { fromCommunity }
  } = useRouter()

  const user = useSelector(getUser)

  const onChangeCommunityLevel: ChangeCommunityLevelFunc = async (
    ccId,
    name,
    value
  ) => {
    try {
      dispatch(setLoading(true))
      await changeCommunityLevel(userId, ccId, {
        community_level: value
      })
      if (!value) {
        notification.success({
          message: `You successfully unassign the contract category ${name}.`
        })
      }
      await onUpdateCCList()
    } catch (err) {
      const error = err as AxiosResponse<{ community_level?: string[] }>
      if (role === ROLES.CONTRACT_STEWARD) {
        notification.error({
          message: `To unassign an active category, first you must reassign the contract category ${name} to another contract steward`
        })
      } else if (error?.data?.community_level?.length) {
        notification.error({
          message: error?.data.community_level[0]
        })
      }
      dispatch(setLoading(false))
    }
  }

  const onDeleteCCPopupOpen = useCallback(
    (id) => {
      setSingleSelectedCC(id)
      deleteCCPopup.actions.open()
    },
    [deleteCCPopup]
  )

  // extended mode (with community level checkboxes) is only for 3 cases - user is on his profile / CS sees another CS / SL sees another SL
  const extendedViewModeOnly =
    userId === user.uuid || (!!fromCommunity && canSeeExtendedViewMode)

  const userCanManageCategoryAssignment =
    canCreateCommunityContractCategories || canViewCommunityContractCategories

  const tableColumns = useMemo(
    () =>
      role
        ? columns({
            role,
            onChangeCommunityLevel,
            setSelectedCC,
            onDeleteCC: onDeleteCCPopupOpen,
            canManageContractCategories,
            categoryAssignmentVisible: canManageCategoryReassignment,
            communityLevelCheckboxesVisible: canViewCommunityLevelCheckboxes,
            communityLevelCheckboxesEditable: canEditCommunityLevelCheckboxes,
            userRole: user.role,
            extendedViewModeOnly,
            simpleViewModeOnly
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, user.role]
  )

  const handleChangePageSize = async (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    await getContractCategories({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const handleTableChange = async (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    await getContractCategories({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const onUpdateCCList = useCallback(async () => {
    const page = {
      pageNumber: 1,
      pageSize: pageInfo.pageSize
    }
    await getContractCategories({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }, [getContractCategories, pageInfo.pageSize, setPageInfo, sortParams])

  const onOpenAddModal = () => setIsAddModalOpen(true)

  const onDeleteCC = useCallback(async () => {
    if (!selectedRowKeys && !singleSelectedCC) return
    try {
      dispatch(setLoading(true))
      await removeCCFromUserProfile(
        userId,
        singleSelectedCC ? [singleSelectedCC] : selectedRowKeys
      )
      await onUpdateCCList()
      deleteCCPopup.actions.close()
      notification.success({
        message: `Contract ${
          singleSelectedCC ? 'category has' : 'categories have'
        } been successfully removed.`
      })
      setSelectedRowKeys([])
      setSingleSelectedCC(null)
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [
    deleteCCPopup,
    dispatch,
    selectedRowKeys,
    onUpdateCCList,
    userId,
    singleSelectedCC
  ])

  const onCancelDeleteCallback = useCallback(() => {
    setSingleSelectedCC(null)
  }, [])

  const handleSelectRow = (selectedRowKeys) =>
    setSelectedRowKeys(selectedRowKeys)

  return (
    <div className="user-contract-categories">
      <div className="row space-between full-width mb-4">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: PLACEHOLDERS.SEARCH,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
        <div className="row justify-end">
          {!extendedViewModeOnly &&
            !simpleViewModeOnly &&
            userCanManageCategoryAssignment &&
            (selectedRowKeys.length ? (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Delete16 />}
                onClick={deleteCCPopup.actions.open}
                upperCase
                danger
              >
                {BTN_TXT.DELETE}
              </Button>
            ) : (
              <Button
                onClick={onOpenAddModal}
                type={BUTTON_TYPES.PRIMARY}
                upperCase
              >
                {BTN_TXT.ASSIGN_CONTRACT_CATEGORY}
              </Button>
            ))}
        </div>
      </div>
      {!firstLoad && (
        <Table
          dataSource={tableData}
          className="user-contract-categories-table"
          columns={tableColumns}
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          hasSearchOrFilters={!!debouncedSearch}
          createButton={
            !extendedViewModeOnly && !simpleViewModeOnly && !debouncedSearch
              ? {
                  buttonText: BTN_TXT.ASSIGN_CONTRACT_CATEGORY,
                  availability: true,
                  action: onOpenAddModal
                }
              : undefined
          }
          rowSelection={
            canManageContractCategories ||
            extendedViewModeOnly ||
            simpleViewModeOnly
              ? undefined
              : {
                  selectedRowKeys,
                  onChange: handleSelectRow
                }
          }
        />
      )}
      <AddCCPopup
        isOpen={isAddModalOpen}
        userId={userId}
        setIsOpen={setIsAddModalOpen}
        onUpdateCCList={onUpdateCCList}
      />
      <ManageAssignmentWithCoOrRFPHSPresidentPopup
        selectedCC={selectedCC}
        userName={userName}
      />
      <ManageAssignmentPopupWithoutItems
        selectedCC={selectedCC}
        userName={userName}
      />
      <DeleteContractCategoriesPopup
        onDelete={onDeleteCC}
        isMultiple={!singleSelectedCC && selectedRowKeys.length > 1}
        onCancelCallback={onCancelDeleteCallback}
      />
    </div>
  )
}
